/*
 * Builds the sandboxed-iframe HTML document that runs a browser-metro bundle.
 * Ported from reactnative.run's playground harness (`example/src/App.tsx`, MIT):
 *   - forwards the app's `console.*` to the parent via postMessage
 *   - installs `window.onerror` / `unhandledrejection` handlers that resolve
 *     stack frames back to original source files using the inline source map
 *   - optionally wires expo-router `+api` routes through a fetch interceptor
 *
 * The parent (`browserMetroRunner.client.ts`) listens for the posted `console`
 * and `runtime-error` messages and feeds them into the RN runner store.
 */

export interface InlineSourceMap {
  sources: string[];
  mappings: string;
}

/** Extract the trailing inline base64 source map from a bundle, if present. */
export function extractInlineSourceMap(code: string): InlineSourceMap | null {
  const marker = '//# sourceMappingURL=data:application/json;base64,';
  const idx = code.lastIndexOf(marker);

  if (idx === -1) {
    return null;
  }

  const start = idx + marker.length;
  let end = code.indexOf('\n', start);

  if (end === -1) {
    end = code.length;
  }

  const b64 = code.slice(start, end).trim();

  try {
    const json = JSON.parse(atob(b64));
    return { sources: json.sources || [], mappings: json.mappings || '' };
  } catch {
    return null;
  }
}

// Script 1: console interception — forwards console.* to the parent window.
const CONSOLE_SCRIPT =
  "['log','warn','error','info'].forEach(function(method) {\n" +
  '  var orig = console[method];\n' +
  '  console[method] = function() {\n' +
  '    var args = Array.prototype.slice.call(arguments);\n' +
  '    var text = args.map(function(a) {\n' +
  "      if (typeof a === 'object') try { return JSON.stringify(a); } catch(e) { return String(a); }\n" +
  '      return String(a);\n' +
  "    }).join(' ');\n" +
  "    window.parent.postMessage({ type: 'console', method: method, text: text }, '*');\n" +
  '    if (orig) orig.apply(console, arguments);\n' +
  '  };\n' +
  '});\n';

/*
 * Script 2: reanimated worklet marker.
 *
 * Reanimated only ever asks "is this a worklet?" through `fn.__workletHash`
 * (`isWorkletFunction` in `commonTypes`), and compares captured values through
 * `fn.__closure`. Both are normally attached by `react-native-reanimated/plugin`
 * at build time — a Babel plugin we cannot run in the browser worker (see
 * `plugins/reanimated.ts`), so every function in the preview looks like a
 * non-worklet.
 *
 * Reanimated's web build is explicitly designed to run WITHOUT that plugin
 * ("web without the Babel plugin" — pass dependency arrays instead), and its web
 * paths run handlers straight on the JS thread. But `useHandler` still asserts
 * worklet-ness unconditionally, so `useAnimatedScrollHandler` /
 * `useAnimatedGestureHandler` crash with "[Reanimated] Passed a function that is
 * not a worklet". Reanimated's own internal worklets (e.g. `Easing.bezier`) are
 * unmarked here for the same reason.
 *
 * Marking every function satisfies those assertions, which is sound on web where
 * there is no separate UI runtime to serialise worklets into. The hash is
 * derived from the function source so reanimated's dependency comparison stays
 * meaningful across renders; `__closure` is empty, which is exactly the
 * plugin-less web contract — hooks that capture React state need an explicit
 * dependency array.
 *
 * Both properties are non-enumerable and writable, so code that assigns a real
 * hash/closure (a future transform, or a pre-compiled package) still wins.
 */
export const WORKLET_SHIM_SCRIPT =
  '(function() {\n' +
  "  if (Object.getOwnPropertyDescriptor(Function.prototype, '__workletHash')) return;\n" +
  '  function own(fn, key, value) {\n' +
  '    try { Object.defineProperty(fn, key, { value: value, writable: true, configurable: true }); } catch (e) {}\n' +
  '    return value;\n' +
  '  }\n' +
  '  function hashOf(fn) {\n' +
  "    var src = '';\n" +
  '    try { src = Function.prototype.toString.call(fn); } catch (e) {}\n' +
  '    var h = 5381;\n' +
  '    for (var i = 0; i < src.length; i++) { h = ((h << 5) + h + src.charCodeAt(i)) | 0; }\n' +
  '    return h || 1;\n' +
  '  }\n' +
  "  Object.defineProperty(Function.prototype, '__workletHash', {\n" +
  '    configurable: true,\n' +
  "    get: function() { return own(this, '__workletHash', hashOf(this)); },\n" +
  "    set: function(v) { own(this, '__workletHash', v); }\n" +
  '  });\n' +
  "  Object.defineProperty(Function.prototype, '__closure', {\n" +
  '    configurable: true,\n' +
  "    get: function() { return own(this, '__closure', {}); },\n" +
  "    set: function(v) { own(this, '__closure', v); }\n" +
  '  });\n' +
  '})();\n';

// Script 3: source-map resolver + error handlers (ES5-compatible, self-contained).
const SM_RESOLVER_SCRIPT =
  '(function() {\n' +
  "var B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';\n" +
  'var B64D = {};\n' +
  'for (var i = 0; i < B64.length; i++) B64D[B64[i]] = i;\n' +
  'function decodeVLQ(str, offset) {\n' +
  '  var result = 0, shift = 0, cont, idx = offset;\n' +
  '  do {\n' +
  '    var d = B64D[str[idx++]];\n' +
  '    cont = (d & 32) !== 0;\n' +
  '    result += (d & 31) << shift;\n' +
  '    shift += 5;\n' +
  '  } while (cont);\n' +
  '  return { value: (result & 1) ? -(result >> 1) : (result >> 1), next: idx };\n' +
  '}\n' +
  'function decodeMappings(mappings) {\n' +
  '  var lines = [], srcIdx = 0, origLine = 0, origCol = 0;\n' +
  "  var parts = mappings.split(';');\n" +
  '  for (var li = 0; li < parts.length; li++) {\n' +
  '    var segs = [], genCol = 0, lineStr = parts[li];\n' +
  '    if (lineStr) {\n' +
  "      var segParts = lineStr.split(',');\n" +
  '      for (var si = 0; si < segParts.length; si++) {\n' +
  '        var s = segParts[si]; if (!s) continue;\n' +
  '        var pos = 0, f = [];\n' +
  '        while (pos < s.length) { var r = decodeVLQ(s, pos); f.push(r.value); pos = r.next; }\n' +
  '        if (f.length >= 4) {\n' +
  '          genCol += f[0]; srcIdx += f[1]; origLine += f[2]; origCol += f[3];\n' +
  '          segs.push([genCol, srcIdx, origLine, origCol]);\n' +
  '        } else if (f.length >= 1) {\n' +
  '          genCol += f[0]; segs.push([genCol]);\n' +
  '        }\n' +
  '      }\n' +
  '    }\n' +
  '    lines.push(segs);\n' +
  '  }\n' +
  '  return lines;\n' +
  '}\n' +
  'var maps = {};\n' +
  'function addMap(url, mapData) {\n' +
  '  maps[url] = { sources: mapData.sources, decoded: decodeMappings(mapData.mappings) };\n' +
  '}\n' +
  'function resolve(url, line, col) {\n' +
  '  var m = maps[url];\n' +
  '  if (!m) return null;\n' +
  '  var decoded = m.decoded;\n' +
  '  if (line < 0 || line >= decoded.length) return null;\n' +
  '  var segs = decoded[line];\n' +
  '  if (!segs || segs.length === 0) return null;\n' +
  '  var best = null;\n' +
  '  for (var i = 0; i < segs.length; i++) {\n' +
  '    var seg = segs[i];\n' +
  '    if (seg.length < 4) continue;\n' +
  '    if (seg[0] <= col) best = seg;\n' +
  '  }\n' +
  '  if (!best) {\n' +
  '    for (var j = 0; j < segs.length; j++) { if (segs[j].length >= 4) { best = segs[j]; break; } }\n' +
  '  }\n' +
  '  if (!best) return null;\n' +
  "  return { file: m.sources[best[1]] || '(unknown)', line: best[2] + 1, column: best[3] + 1 };\n" +
  '}\n' +
  'function extractInlineSM(code) {\n' +
  "  var marker = '//# sourceMappingURL=data:application/json;base64,';\n" +
  '  var idx = code.lastIndexOf(marker);\n' +
  '  if (idx === -1) return null;\n' +
  '  var start = idx + marker.length;\n' +
  "  var end = code.indexOf('\\n', start);\n" +
  '  if (end === -1) end = code.length;\n' +
  '  try { return JSON.parse(atob(code.slice(start, end).trim())); } catch(e) { return null; }\n' +
  '}\n' +
  'function extractSourceURL(code) {\n' +
  "  var marker = '//# sourceURL=';\n" +
  '  var idx = code.lastIndexOf(marker);\n' +
  '  if (idx === -1) return null;\n' +
  '  var start = idx + marker.length;\n' +
  "  var end = code.indexOf('\\n', start);\n" +
  '  if (end === -1) end = code.length;\n' +
  '  return code.slice(start, end).trim();\n' +
  '}\n' +
  'window.__SM = {\n' +
  '  init: function(url, mapData) { addMap(url, mapData); },\n' +
  '  add: function(url, mapData) { addMap(url, mapData); },\n' +
  '  resolve: resolve\n' +
  '};\n' +
  "window.addEventListener('message', function(e) {\n" +
  "  if (!e.data || e.data.type !== 'hmr-update') return;\n" +
  '  var mods = e.data.updatedModules;\n' +
  '  if (!mods) return;\n' +
  '  for (var key in mods) {\n' +
  '    var code = mods[key];\n' +
  "    if (typeof code !== 'string') continue;\n" +
  '    var sm = extractInlineSM(code);\n' +
  '    var sourceURL = extractSourceURL(code);\n' +
  '    if (sm && sourceURL) {\n' +
  '      window.__SM.add(sourceURL, sm);\n' +
  '    }\n' +
  '  }\n' +
  '});\n' +
  'function parseStack(stack) {\n' +
  '  if (!stack) return [];\n' +
  '  var frames = [];\n' +
  "  var lines = stack.split('\\n');\n" +
  '  for (var i = 0; i < lines.length; i++) {\n' +
  '    var line = lines[i].trim();\n' +
  '    var m = line.match(/^at\\s+(.+?)\\s+\\((.+?):(\\d+):(\\d+)\\)$/);\n' +
  '    if (m) { frames.push({ fn: m[1], file: m[2], line: parseInt(m[3],10), column: parseInt(m[4],10) }); continue; }\n' +
  '    m = line.match(/^at\\s+(.+?):(\\d+):(\\d+)$/);\n' +
  "    if (m) { frames.push({ fn: '(anonymous)', file: m[1], line: parseInt(m[2],10), column: parseInt(m[3],10) }); }\n" +
  '  }\n' +
  '  return frames;\n' +
  '}\n' +
  'function resolvePosition(url, line, col) {\n' +
  '  var resolved = resolve(url, line - 1, col - 1);\n' +
  '  if (resolved) return resolved;\n' +
  '  return { file: url, line: line, column: col };\n' +
  '}\n' +
  'function sendError(msg, url, line, col, err) {\n' +
  "  var pos = resolvePosition(url || '', line || 0, col || 0);\n" +
  '  var rawFrames = parseStack(err && err.stack);\n' +
  '  var resolvedFrames = [];\n' +
  '  for (var i = 0; i < rawFrames.length; i++) {\n' +
  '    var f = rawFrames[i];\n' +
  '    var rp = resolve(f.file, f.line - 1, f.column - 1);\n' +
  '    if (rp) resolvedFrames.push({ fn: f.fn, file: rp.file, line: rp.line, column: rp.column });\n' +
  '    else resolvedFrames.push(f);\n' +
  '  }\n' +
  '  window.parent.postMessage({\n' +
  "    type: 'runtime-error',\n" +
  "    message: typeof msg === 'string' ? msg : String(msg),\n" +
  '    file: pos.file,\n' +
  '    line: pos.line,\n' +
  '    column: pos.column,\n' +
  '    stack: resolvedFrames\n' +
  "  }, '*');\n" +
  '}\n' +
  'window.onerror = function(msg, url, line, col, err) {\n' +
  '  sendError(msg, url, line, col, err);\n' +
  '};\n' +
  "window.addEventListener('unhandledrejection', function(e) {\n" +
  '  var err = e.reason;\n' +
  '  var msg = (err && err.message) ? err.message : String(err);\n' +
  "  sendError('Unhandled Promise Rejection: ' + msg, '', 0, 0, err instanceof Error ? err : null);\n" +
  '});\n' +
  '})();\n';

function buildApiScripts(apiBlobUrl: string): string {
  return (
    '<script src="' +
    apiBlobUrl +
    '"></' +
    'script>\n' +
    '<script>\n' +
    '(function() {\n' +
    '  var api = window.__API_ROUTES__;\n' +
    '  if (!api) return;\n' +
    '  var _origFetch = window.fetch;\n' +
    '  window.fetch = function(input, init) {\n' +
    "    var url = typeof input === 'string' ? input : input.url;\n" +
    '    var pathname = url;\n' +
    "    if (url.indexOf('://') !== -1) {\n" +
    '      try { pathname = new URL(url).pathname; } catch(e) {}\n' +
    "    } else if (url.indexOf('?') !== -1) {\n" +
    "      pathname = url.split('?')[0];\n" +
    '    }\n' +
    '    if (!api) return _origFetch.apply(this, arguments);\n' +
    '    var match = api.match(pathname);\n' +
    '    if (!match) return _origFetch.apply(this, arguments);\n' +
    "    var method = ((init && init.method) || 'GET').toUpperCase();\n" +
    '    var handler = match.handler[method];\n' +
    "    if (!handler) return Promise.resolve(new Response('Method not allowed', { status: 405 }));\n" +
    "    var request = new Request('http://localhost' + pathname, init);\n" +
    '    try {\n' +
    '      var result = handler(request);\n' +
    '      return result instanceof Promise ? result : Promise.resolve(result);\n' +
    '    } catch(err) {\n' +
    '      return Promise.resolve(new Response(JSON.stringify({ error: err.message }), { status: 500 }));\n' +
    '    }\n' +
    '  };\n' +
    '})();\n' +
    '</' +
    'script>\n'
  );
}

/**
 * Assemble the full HTML document for the preview iframe.
 *
 * @param jsBlobUrl   blob URL of the client bundle
 * @param sourceMap   inline source map extracted from the bundle (or null)
 * @param apiBlobUrl  optional blob URL of the expo-router `+api` bundle
 */
export function buildPreviewHtml(jsBlobUrl: string, sourceMap: InlineSourceMap | null, apiBlobUrl?: string): string {
  let smInitScript = '';

  if (sourceMap) {
    const smJson = JSON.stringify({ sources: sourceMap.sources, mappings: sourceMap.mappings });
    smInitScript = '<script>window.__SM.init(' + JSON.stringify(jsBlobUrl) + ',' + smJson + ');</' + 'script>\n';
  }

  const apiScripts = apiBlobUrl ? buildApiScripts(apiBlobUrl) : '';

  return (
    "<!DOCTYPE html><html><head><meta charset='UTF-8'>" +
    "<script async src='https://cdn.tailwindcss.com'></" +
    'script>' +
    '<style>html,body,#root{height:100%;margin:0}body{overflow:hidden}#root{display:flex;flex-direction:column}' +
    '::-webkit-scrollbar{width:8px;height:8px}::-webkit-scrollbar-track{background:transparent}' +
    '::-webkit-scrollbar-thumb{background:#3f3f46;border-radius:4px}::-webkit-scrollbar-thumb:hover{background:#52525b}' +
    '::-webkit-scrollbar-corner{background:transparent}*{scrollbar-color:#3f3f46 transparent}</style></head>' +
    "<body><div id='root'></div><script>\n" +
    CONSOLE_SCRIPT +
    WORKLET_SHIM_SCRIPT +
    SM_RESOLVER_SCRIPT +
    '</' +
    'script>\n' +
    smInitScript +
    apiScripts +
    '<script src="' +
    jsBlobUrl +
    '"></' +
    'script>\n' +
    '</body></html>'
  );
}
